import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  Label,
  Row,
  Col,
  Button,
  FormFeedback,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { Country, State, City } from "country-state-city";
import { addMentor, editMentor } from "utils/apiService";
import { toast } from "react-toastify";
import { getCountryCallingCodes } from "../../utils/phoneUtils";

interface SelectOption {
  value: string;
  label: string;
}

const TeamsModal = ({ isOpen, toggle, mentor, isEdit, refreshData }) => {
  const [countries, setCountries] = useState<SelectOption[]>([]);
  const [states, setStates] = useState<SelectOption[]>([]);
  const [cities, setCities] = useState<SelectOption[]>([]);
  const [cvFile, setCvFile] = useState(null);

  useEffect(() => {
    const countryList = Country.getAllCountries().map((country) => ({
      value: country.isoCode,
      label: country.name,
    }));
    setCountries(countryList);
  }, [countries]);

  const fetchStates = (countryCode: string | undefined) => {
    if (!countryCode) return;
    const stateList = State.getStatesOfCountry(countryCode).map((state) => ({
      value: state.isoCode,
      label: state.name,
    }));
    setStates(stateList);
    setCities([]);
  };

  const fetchCities = (
    countryCode: string | undefined,
    stateCode: string | undefined
  ) => {
    if (!countryCode || !stateCode) return;
    const cityList = City.getCitiesOfState(countryCode, stateCode).map(
      (city) => ({
        value: city.name,
        label: city.name,
      })
    );
    setCities(cityList);
  };

  console.log(mentor?.state);
  console.log(mentor);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: mentor?.user_id || "",
      name: mentor?.user_name || "",
      email: mentor?.user_email || "",
      phoneNumber: mentor?.phone_number || "",
      address: mentor?.address || "",
      country: mentor?.country || "",
      state: mentor?.state || "",
      city: mentor?.city || "",
      qualification: mentor?.qualification || "",
      teachingExperience: mentor?.teaching_experience || "",
      jobType: mentor?.job_type || "",
      cv: mentor?.cv || "",
      password: "",
      confirmPassword: "",
      countryCode: mentor?.country_code
        ? `+${mentor.country_code.replace(/\D/g, "")}`
        : "+91",
      status: mentor?.status || "pending",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      countryCode: Yup.string().required("Country code is required"),
      phoneNumber: Yup.string().required("Phone number is required"),
      address: Yup.string().required("Address is required"),
      qualification: Yup.string().required("Qualification is required"),
      teachingExperience: Yup.string().required(
        "Teaching experience is required"
      ),
      jobType: Yup.string().required("Job type is required"),
      country: Yup.string().required("Country is required"),
      state: Yup.string().required("State is required"),
      city: Yup.string().required("City is required"),
      ...(isEdit
        ? {}
        : {
          password: Yup.string()
            .matches(
              /^(?=.*\d)(?=.*[@$#!%?&])[A-Za-z\d@$#!%?&]{8,}$/,
              "Password must be at least 8 characters long, contain at least one number and one special character."
            )
            .required("Password is required"),
          confirmPassword: Yup.string()
            .oneOf([Yup.ref("password")], "Passwords must match")
            .required("Confirm Password is required"),
        }),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        Object.keys(values).forEach((key) => {
          if (key === "cv") return;
          if (key === "countryCode") {
            const digitsOnly = values[key].replace(/\D/g, "");
            formData.append("countryCode", digitsOnly);
          } else {
            formData.append(key, values[key]);
          }
        });

        if (cvFile) {
          formData.append("cv", cvFile);
        }
        //console.log("FormData", formData);
        if (isEdit) {
          console.log("Editing Mentor:", values);
          await editMentor(formData);
          toast.success("Mentor details updated successfully!");
        } else {
          console.log("Adding Mentor:", values);
          await addMentor(formData);
          toast.success("Mentor added successfully!");
          resetForm();
        }

        toggle();
        refreshData();
      } catch (err) {
        console.error("Error saving mentor:", err);
      }
    },
  });

  useEffect(() => {
    const countryISO = countries.find(
      (c) => c.label === formik.values.country
    )?.value;
    if (countryISO) {
      console.log("Fetching states for country ISO:", countryISO);
      fetchStates(countryISO);
    }
  }, [formik.values.country]);

  useEffect(() => {
    const countryISO = countries.find(
      (c) => c.label === formik.values.country
    )?.value;
    const stateISO = states.find((s) => s.label === formik.values.state)?.value;

    if (countryISO && stateISO) {
      console.log("Fetching cities for state ISO:", stateISO);
      fetchCities(countryISO, stateISO);
    }
  }, [formik.values.state, states, countries, formik.values.country]);

  const handleCvUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      formik.setFieldValue("cv", file);
      //console.log("Came here!", file);
      setCvFile(file);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        {isEdit ? "Edit Mentor" : "Add Mentor"}
      </ModalHeader>
      <ModalBody>
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label>Name</Label>
                <Input
                  {...formik.getFieldProps("name")}
                  invalid={formik.touched.name && !!formik.errors.name}
                />
                <FormFeedback>
                  {formik.errors.name ? String(formik.errors.name) : ""}
                </FormFeedback>
              </div>

              <div className="mb-3">
                <Label>Email</Label>
                <Input
                  {...formik.getFieldProps("email")}
                  invalid={formik.touched.email && !!formik.errors.email}
                />
                <FormFeedback>
                  {formik.errors.email ? String(formik.errors.email) : ""}
                </FormFeedback>
              </div>

              <Row className="mb-3">
                <Col md={6}>
                  <Label>Country Code</Label>
                  <Select
                    options={getCountryCallingCodes()}
                    onChange={(selected) =>
                      formik.setFieldValue("countryCode", selected?.value)
                    }
                    value={getCountryCallingCodes().find(
                      (c) => c.value === formik.values.countryCode
                    )}
                    styles={{
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                        backgroundColor: "#f8f9fa",
                        boxShadow: "0px 2px 10px rgba(0,0,0,0.15)",
                      }),
                    }}
                  />
                </Col>

                <Col md={6}>
                  <Label>Phone Number</Label>
                  <Input
                    {...formik.getFieldProps("phoneNumber")}
                    invalid={
                      formik.touched.phoneNumber && !!formik.errors.phoneNumber
                    }
                  />
                  <FormFeedback>
                    {formik.errors.phoneNumber
                      ? String(formik.errors.phoneNumber)
                      : ""}
                  </FormFeedback>
                </Col>
              </Row>

              <div className="mb-3">
                <Label>Address</Label>
                <Input
                  {...formik.getFieldProps("address")}
                  invalid={formik.touched.address && !!formik.errors.address}
                />
                <FormFeedback>
                  {formik.errors.address ? String(formik.errors.address) : ""}
                </FormFeedback>
              </div>

              <div className="mb-3">
                <Label>Job Type</Label>
                <Select
                  options={[
                    { value: "Full-Time", label: "Full-Time" },
                    { value: "Part-Time", label: "Part-Time" },
                  ]}
                  onChange={(selected) =>
                    formik.setFieldValue("jobType", selected?.value)
                  }
                  value={
                    [
                      { value: "Full-Time", label: "Full-Time" },
                      { value: "Part-Time", label: "Part-Time" },
                    ].find(
                      (option) => option.value === formik.values.jobType
                    ) || null
                  }
                />
              </div>

              <div className="mb-3">
                <Label>Teaching Experience</Label>
                <Input
                  {...formik.getFieldProps("teachingExperience")}
                  invalid={
                    formik.touched.teachingExperience &&
                    !!formik.errors.teachingExperience
                  }
                />
                <FormFeedback>
                  {formik.errors.teachingExperience
                    ? String(formik.errors.teachingExperience)
                    : ""}
                </FormFeedback>
              </div>
              {!isEdit && (
                <div className="mb-3">
                  <Label>Qualification with background</Label>
                  <Input
                    {...formik.getFieldProps("qualification")}
                    invalid={
                      formik.touched.qualification &&
                      !!formik.errors.qualification
                    }
                  />
                  <FormFeedback>
                    {formik.errors.qualification
                      ? String(formik.errors.qualification)
                      : ""}
                  </FormFeedback>
                </div>
              )}
            </Col>

            <Col md={6}>
              <div className="mb-3">
                <Label>Country</Label>
                <Select
                  options={countries}
                  onChange={(selected) => {
                    formik.setFieldValue("country", selected?.label || "");
                    fetchStates(selected?.value);
                  }}
                  value={
                    countries.find((c) => c.label === formik.values.country) ||
                    null
                  }
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      backgroundColor: "white",
                      maxHeight: "150px",
                      overflowY: "auto",
                    }),
                  }}
                />
              </div>

              <div className="mb-3">
                <Label>State</Label>
                <Select
                  options={states}
                  onChange={(selected) => {
                    formik.setFieldValue("state", selected?.label || "");
                    fetchCities(
                      countries.find((c) => c.label === formik.values.country)
                        ?.value,
                      selected?.value
                    );
                  }}
                  value={
                    states.find((s) => s.label === formik.values.state) || null
                  }
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      backgroundColor: "white",
                      maxHeight: "150px",
                      overflowY: "auto",
                    }),
                  }}
                />
              </div>

              <div className="mb-3">
                <Label>City</Label>
                <Select
                  options={cities}
                  onChange={(selected) =>
                    formik.setFieldValue("city", selected?.label || "")
                  }
                  value={
                    cities.find((c) => c.label === formik.values.city) || null
                  }
                  styles={{
                    menu: (provided) => ({
                      ...provided,
                      zIndex: 9999,
                      backgroundColor: "white",
                      maxHeight: "150px",
                      overflowY: "auto",
                    }),
                  }}
                />
              </div>

              {!isEdit && (
                <>
                  <div className="mb-3">
                    <Label>Password</Label>
                    <Input
                      type="password"
                      {...formik.getFieldProps("password")}
                      invalid={
                        formik.touched.password && !!formik.errors.password
                      }
                    />
                    <FormFeedback>
                      {formik.errors.password
                        ? String(formik.errors.password)
                        : ""}
                    </FormFeedback>
                  </div>

                  <div className="mb-3">
                    <Label>Confirm Password</Label>
                    <Input
                      type="password"
                      {...formik.getFieldProps("confirmPassword")}
                      invalid={
                        formik.touched.confirmPassword &&
                        !!formik.errors.confirmPassword
                      }
                    />
                    <FormFeedback>
                      {formik.errors.confirmPassword
                        ? String(formik.errors.confirmPassword)
                        : ""}
                    </FormFeedback>
                  </div>
                </>
              )}
              <div className="mb-3">
                <Label>CV</Label>
                <Input
                  type="file"
                  onChange={handleCvUpload}
                  accept=".pdf,.docx"
                />

                {isEdit && !formik.values.cv && (
                  <div className="mt-2 view-cv text-end">
                    <a
                      href={formik.values.cv}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      View CV
                    </a>
                  </div>
                )}
              </div>
              {isEdit && mentor.cv_mimetype !== null && (
                <div className="mt-2 view-cv">
                  <button
                    className="btn btn-link text-primary"
                    style={{ padding: "0px", paddingBottom: "10px" }}
                    type="button"
                    onClick={() => {
                      const fileUrl = `data:${mentor.cv_mimetype};base64,${mentor.cv_base64}`;
                      const newTab = window.open();
                      if (newTab) {
                        newTab.document.write(
                          `<iframe src="${fileUrl}" width="100%" height="100%" style="border:none;"></iframe>`
                        );
                        newTab.document.close();
                      }
                    }}
                  >
                    📄 View Uploaded CV
                  </button>
                </div>
              )}
              {isEdit && (
                <div className="mb-3">
                  <Label>Qualification with background</Label>
                  <Input
                    {...formik.getFieldProps("qualification")}
                    invalid={
                      formik.touched.qualification &&
                      !!formik.errors.qualification
                    }
                  />
                  <FormFeedback>
                    {formik.errors.qualification
                      ? String(formik.errors.qualification)
                      : ""}
                  </FormFeedback>
                </div>
              )}

             {/*  {isEdit && (

                <div className="mb-3">
                  <Label>Status</Label>
                  <Input
                    type="select"
                    name="status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </Input>
                </div>
              )} */}

            </Col>
          </Row>


          <div className="text-end">
            <Button type="submit" color="success">
              {isEdit ? "Update" : "Add"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default TeamsModal;
